import React from "react";
import { Box, Stack, Switch } from "@mui/material";
import { tokens, radius } from "../theme";

/**
 * Refined, reusable building blocks for the attendance create/edit wizards so
 * every step shares one design language (matching the People module): white
 * section cards on a neutral surface, consistent field labels, toggle rows,
 * and a standard footer. Keep logic in the pages — these are presentational.
 */

// A titled white card that groups related fields.
export const FormSection = ({ title, description, children, sx }) => (
  <Box sx={{
    background: tokens.card,
    border: `1px solid ${tokens.border}`,
    borderRadius: `${radius.card}px`,
    p: "1.5rem",
    mb: "1.25rem",
    ...sx,
  }}>
    {(title || description) && (
      <Box sx={{ mb: "1.25rem" }}>
        {title && <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{title}</Box>}
        {description && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>{description}</Box>}
      </Box>
    )}
    {children}
  </Box>
);

// Consistent label sitting above a field.
export const FieldLabel = ({ children, required }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
    {children}{required && <Box component="span" sx={{ color: tokens.danger, ml: "0.25rem" }}>*</Box>}
  </Box>
);

// Two-column responsive grid for form fields.
export const FieldGrid = ({ children, columns = 2 }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, 1fr)` }, gap: "1.25rem" }}>
    {children}
  </Box>
);

// A toggle/switch row: title + description on the left, MUI Switch on the right.
export const SwitchRow = ({ title, description, checked, onChange, disabled }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between"
    sx={{ py: "1rem", borderTop: `1px solid ${tokens.border}`, "&:first-of-type": { borderTop: "none" } }}>
    <Box sx={{ pr: "1rem" }}>
      <Box sx={{ fontSize: "0.875rem", fontWeight: 500, color: tokens.text }}>{title}</Box>
      {description && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>{description}</Box>}
    </Box>
    <Switch checked={!!checked} onChange={onChange} disabled={disabled} />
  </Stack>
);

/**
 * The footer of a stepped page.
 *
 * `draftNote` is the book's requirement that "the draft says plainly that
 * nothing has taken effect yet" — the sentence sits between Back and the
 * forward actions, where someone deciding whether it is safe to leave will
 * actually read it. Without it, a half-finished wizard looks exactly like a
 * half-finished record, and people either abandon work they could have kept or
 * assume something took effect that did not.
 */
export const WizardActions = ({ children, draftNote, back }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mt: "2rem", flexWrap: "wrap", rowGap: "0.75rem" }}
  >
    {back}
    {draftNote && (
      <Box sx={{ flex: 1, minWidth: "12rem", fontSize: "0.8125rem", color: tokens.textMuted, textAlign: "center" }}>
        {draftNote}
      </Box>
    )}
    <Box sx={{ flex: draftNote ? "none" : 1 }} />
    {children}
  </Stack>
);

// Page shell: a single bordered card holding the stepper row, content and footer.
export const WizardCard = ({ children, sx }) => (
  <Box sx={{
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: "12px",
    p: { xs: "1.25rem", md: "1.75rem" },
    ...sx,
  }}>
    {children}
  </Box>
);
